import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { subscriptionService, type SubscriptionCategory } from '@/services/subscription.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues { name: string; description: string; color: string }

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  category?: SubscriptionCategory;
  onClose: () => void;
  onSuccess: () => void;
}

export function CategoryFormDialog({ open, mode, category, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { name: '', description: '', color: '#6B7280' },
  });

  const color = watch('color');

  useEffect(() => {
    if (open) {
      reset({
        name: category?.name ?? '',
        description: category?.description ?? '',
        color: category?.color ?? '#6B7280',
      });
    }
  }, [open, category, reset]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      isEdit && category
        ? subscriptionService.updateCategory(category.id, { name: v.name.trim(), description: v.description.trim() || undefined, color: v.color })
        : subscriptionService.createCategory({ name: v.name.trim(), description: v.description.trim() || undefined, color: v.color }),
    onSuccess: () => {
      toast.success(isEdit ? 'Category updated' : 'Category created');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'categories'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Category' : 'New Category'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cat-name">Name *</Label>
            <Input id="cat-name" {...register('name', { required: 'Name is required' })} placeholder="e.g. SaaS Tools" />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cat-desc">Description</Label>
            <Textarea id="cat-desc" {...register('description')} placeholder="Optional description" rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={color}
                onChange={(e) => setValue('color', e.target.value)}
                className="h-9 w-14 rounded cursor-pointer border border-input bg-background p-0.5"
              />
              <Input value={color} onChange={(e) => setValue('color', e.target.value)} placeholder="#6B7280" className="flex-1" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
